import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Send, Mic, MicOff, X, MessageCircle, Crown, User, Share2, Settings } from 'lucide-react'
import { toast } from 'react-hot-toast'
import QRCode from 'react-qr-code'
import { apiUrl } from '../config/api'
import { Flashcard } from '../components/Flashcard'

interface Message {
  id: string
  senderType: 'owner' | 'guest'
  text: string
  translatedText?: string
  timestamp?: string
  culturalNotes?: string
  meaningExplanation?: string
}

// Lightweight logger controlled by VITE_LOG_LEVEL (debug|info|warn|error)
const LOG_LEVEL = (import.meta as any).env?.VITE_LOG_LEVEL ?? 'info'
const log = {
  debug: (...args: any[]) => { if (LOG_LEVEL === 'debug') console.debug(...args) },
  info:  (...args: any[]) => { if (LOG_LEVEL === 'debug' || LOG_LEVEL === 'info') console.log(...args) },
  warn:  (...args: any[]) => console.warn(...args),
  error: (...args: any[]) => console.error(...args)
}

export const SessionPage: React.FC = () => {
  log.debug('Rendering SessionPage')
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const [session, setSession] = useState<any>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [inputText, setInputText] = useState('')
  const [isConnected, setIsConnected] = useState(false)
  const [isRecording, setIsRecording] = useState(false)
  const [userType, setUserType] = useState<'owner' | 'guest'>('owner')
  const [showSessionControls, setShowSessionControls] = useState(true)
  const [isTranscribing, setIsTranscribing] = useState(false)
  const [currentCardIndex, setCurrentCardIndex] = useState(0)
  const [isFlipped, setIsFlipped] = useState(false)
  const [scamAlert, setScamAlert] = useState<{
    riskLevel: 'low' | 'medium' | 'high';
    warning: string;
    suggestions: string[];
    category?: string;
    latestMessageId?: string;
  } | null>(null)
  const [lastScamCheck, setLastScamCheck] = useState<number>(0)
  const [recordingSeconds, setRecordingSeconds] = useState(0)
  const [justSentId, setJustSentId] = useState<string | null>(null)
  const lastOwnMessageIdRef = useRef<string | null>(null)
  const listEndRef = useRef<HTMLDivElement | null>(null)
  const prevLengthRef = useRef<number>(0)
  const wheelThrottleRef = useRef<number | null>(null)
  const touchStartYRef = useRef<number | null>(null)
  const lastScamKeyRef = useRef<string | null>(null)
  const dismissedScamKeyRef = useRef<string | null>(null)
  const latestMessageIdRef = useRef<string | null>(null)

  const wsRef = useRef<WebSocket | null>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)

  // Serialize sends from transcription to avoid races and lost first message
  const isSendingRef = useRef(false)
  const pendingVoiceQueueRef = useRef<string[]>([])

  const chunksRef = useRef<Blob[]>([])
  const recordingStartedAtRef = useRef<number | null>(null)

  const enqueueTranscribedText = (text: string) => {
    pendingVoiceQueueRef.current.push(text)
    void flushVoiceQueue()
  }

  const flushVoiceQueue = async () => {
    if (isSendingRef.current) return
    isSendingRef.current = true
    try {
      while (pendingVoiceQueueRef.current.length > 0) {
        const next = pendingVoiceQueueRef.current.shift()!
        // First attempt
        let ok = await sendTranscribedMessage(next)
        if (!ok) {
          // Retry once after short delay
          await new Promise(r => setTimeout(r, 500))
          ok = await sendTranscribedMessage(next)
          if (!ok) {
            toast.error('Failed to send voice message')
          }
        }
      }
    } finally {
      isSendingRef.current = false
    }
  }

  // Color schemes based on user type
  const colorScheme = {
    owner: {
      primary: 'indigo',
      secondary: 'purple',
      accent: 'pink',
      background: 'from-indigo-50 to-purple-50',
      cardBg: 'bg-white',
      messageBg: 'bg-gradient-to-r from-indigo-500 to-purple-600',
      messageText: 'text-white',
      borderColor: 'border-indigo-200',
      icon: Crown
    },
    guest: {
      primary: 'teal',
      secondary: 'emerald',
      accent: 'cyan',
      background: 'from-teal-50 to-emerald-50',
      cardBg: 'bg-white',
      messageBg: 'bg-gradient-to-r from-teal-500 to-emerald-600',
      messageText: 'text-white',
      borderColor: 'border-teal-200',
      icon: User
    }
  }

  const currentScheme = colorScheme[userType]

  const actionDisabled = !isConnected || isTranscribing || isRecording || !inputText.trim()
  log.debug('State:', { isConnected, inputText, actionDisabled });

  useEffect(() => {
    if (sessionId) {
      loadSession()
      connectWebSocket()
      
      const isGuest = sessionStorage.getItem(`guest_${sessionId}`) === 'true'
      if (isGuest) {
        setUserType('guest')
      }
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close()
      }
    }
  }, [sessionId])

  // Check if both users are active and hide session controls
  useEffect(() => {
    const hasOwnerMessage = messages.some(msg => msg.senderType === 'owner')
    const hasGuestMessage = messages.some(msg => msg.senderType === 'guest')
    
    if (hasOwnerMessage && hasGuestMessage && showSessionControls) {
      setShowSessionControls(false)
    }
  }, [messages, showSessionControls])

  // Polling for messages: update state only when data changes to avoid jitter
  useEffect(() => {
    if (!sessionId) return

    const pollMessages = async () => {
      try {
        const response = await fetch(apiUrl(`/api/sessions/${sessionId}/messages`))
        if (response.ok) {
          const serverMessages = await response.json()
          setMessages(prevMessages => {
            const prevLen = prevMessages.length
            const nextLen = serverMessages.length
            const prevLastId = prevMessages[prevLen - 1]?.id
            const nextLastId = serverMessages[nextLen - 1]?.id
            // Compute signatures including translatedText so translation updates trigger refresh
            const prevSig = prevMessages.map(m => `${m.id}:${m.translatedText ?? ''}`).join('|')
            const nextSig = serverMessages.map((m: any) => `${m.id}:${m.translatedText ?? ''}`).join('|')
            const changed = prevSig !== nextSig

            if (changed) {
              // If new messages appended, auto-advance and reset flip
              if (nextLen > prevLen || nextLastId !== prevLastId) {
                setCurrentCardIndex(Math.max(0, nextLen - 1))
                setIsFlipped(false)
                // New latest message arrived: clear any previous scam banner tied to old message
                setScamAlert(null)
                lastScamKeyRef.current = null
                dismissedScamKeyRef.current = null
                latestMessageIdRef.current = nextLastId || null
                // Re-run scam analysis immediately for the new message
                setLastScamCheck(0)
                setTimeout(() => { checkForScams() }, 0)
              }
              return serverMessages
            }
            return prevMessages
          })
        }
      } catch (error) {
        console.error('Error polling messages:', error)
      }
    }

    const interval = setInterval(pollMessages, 2000)
    return () => clearInterval(interval)
  }, [sessionId])

  useEffect(() => {
    // Only auto-scroll when new messages were appended
    if (messages.length > prevLengthRef.current) {
      listEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }
    prevLengthRef.current = messages.length
  }, [messages.length])

  const loadSession = async () => {
    try {
      const response = await fetch(apiUrl(`/api/sessions/${sessionId}`))
      if (!response.ok) {
        throw new Error('Session not found')
      }
      const sessionData = await response.json()
      setSession(sessionData)
      
      const messagesResponse = await fetch(apiUrl(`/api/sessions/${sessionId}/messages`))
      if (messagesResponse.ok) {
        const existingMessages = await messagesResponse.json()
        setMessages(existingMessages)
        if (existingMessages.length > 0) {
          setCurrentCardIndex(existingMessages.length - 1)
        }
      }
    } catch (error) {
      console.error('Error loading session:', error)
      toast.error('Failed to load session')
      navigate('/')
    }
  }

  const connectWebSocket = async () => {
    // WebSocket implementation placeholder
    setIsConnected(true)
  }

  const sendMessage = async () => {
    log.debug('Session state in sendMessage:', session);
    log.debug('sendMessage called. inputText:', inputText, 'isConnected:', isConnected);
    if (!inputText.trim() || !sessionId) return

    try {
      // Determine the original language based on user type and session data
      const originalLanguage = userType === 'owner' 
        ? (session?.ownerLanguage || 'English') 
        : (session?.guestLanguage || 'English');

      // Optimistic UI: add a temporary pending message
      const tempId = addTempMessage(inputText.trim(), originalLanguage)

      const response = await fetch(apiUrl(`/api/sessions/${sessionId}/messages`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          text: inputText,
          senderType: userType,
          originalLanguage: originalLanguage
        })
      })

      if (response.ok) {
        setInputText('')
        // Reconcile with server to replace temp message with server version
        await pollAndSyncMessages()
      } else {
        // Remove optimistic on failure
        setMessages(prev => prev.filter(m => (m as any).id !== tempId))
        toast.error('Failed to send message')
      }
    } catch (error) {
      console.error('Error sending message:', error)
      // Remove optimistic on error
      setMessages(prev => prev.filter(m => (m as any)._pending !== true))
      toast.error('Failed to send message')
    }
  }

  const terminateSession = async () => {
    try {
      const response = await fetch(apiUrl(`/api/sessions/${sessionId}`), {
        method: 'DELETE'
      })
      
      if (response.ok) {
        toast.success('Session terminated')
        navigate('/')
      }
    } catch (error) {
      console.error('Error terminating session:', error)
      toast.error('Failed to terminate session')
    }
  }

  const copySessionLink = () => {
    const link = `${window.location.origin}/join/${sessionId}`
    navigator.clipboard.writeText(link)
    toast.success('Session link copied!')
  }

  const startRecording = async () => {
    try {
      if (!navigator.mediaDevices || !window.MediaRecorder) {
        toast.error('Recording not supported in this browser')
        return
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mediaRecorder = new MediaRecorder(stream)
      mediaRecorderRef.current = mediaRecorder

      // Reset and start measuring
      chunksRef.current = []
      recordingStartedAtRef.current = Date.now()
      setRecordingSeconds(0)

      // Start timer for UI
      const timer = setInterval(() => {
        if (recordingStartedAtRef.current) {
          setRecordingSeconds(Math.floor((Date.now() - recordingStartedAtRef.current) / 1000))
        }
      }, 500)

      mediaRecorder.ondataavailable = (e: BlobEvent) => {
        if (e.data && e.data.size > 0) {
          log.debug('Audio data chunk:', e.data.size, 'bytes')
          chunksRef.current.push(e.data)
        }
      }
      mediaRecorder.onstop = async () => {
        clearInterval(timer)
        log.info('Recording stopped, processing audio...', chunksRef.current.length, 'chunks')
        if (chunksRef.current.length === 0) {
          log.warn('No audio chunks recorded.')
          toast.error('No audio captured. Try speaking for a bit longer and ensure mic permissions are allowed.')
          setIsRecording(false)
          return
        }
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' })
        await transcribeAudio(audioBlob)
        setIsRecording(false)
      }

      mediaRecorder.start()
      setIsRecording(true)
      log.info('MediaRecorder started.')
    } catch (err) {
      console.error('Failed to start recording:', err)
      toast.error('Failed to start recording.')
    }
  }

  // Ensures final chunk is flushed before stopping to avoid 0-chunk condition
  const stopRecording = async () => {
    try {
      if (!mediaRecorderRef.current) return
      const rec = mediaRecorderRef.current
      log.info('Stop recording requested...', { hasRecorder: !!rec, isRecording, recorderState: rec.state })

      // Enforce a minimum duration to reduce zero-length blobs
      const minMs = 600
      const startedAt = recordingStartedAtRef.current || Date.now()
      const elapsed = Date.now() - startedAt
      if (elapsed < minMs) {
        await new Promise(r => setTimeout(r, minMs - elapsed))
      }

      if (rec.state !== 'inactive') {
        log.info('Stopping MediaRecorder...')
        // Ask for any pending buffered data before stopping
        try { rec.requestData() } catch {}

        // Wait for the next dataavailable or a short timeout, then stop
        const waitForFinalData = new Promise<void>(resolve => {
          let resolved = false
          const onData = () => {
            if (!resolved) { resolved = true; resolve() }
            rec.removeEventListener('dataavailable', onData as any)
          }
          rec.addEventListener('dataavailable', onData as any, { once: true })
          setTimeout(() => { if (!resolved) resolve() }, 150)
        })
        await waitForFinalData
        rec.stop()
      }
    } catch (e) {
      console.error('Error while stopping recording:', e)
      toast.error('Failed to stop recording')
    }
  }

  const transcribeAudio = async (audioBlob: Blob) => {
    try {
      setIsTranscribing(true)
      log.info('Starting transcription...', { 
        blobSize: audioBlob.size, 
        blobType: audioBlob.type,
        sessionId,
        userLanguage: userType === 'owner' ? session?.ownerLanguage : session?.guestLanguage
      })
      
      const formData = new FormData()
      // Use a unique filename to avoid any intermediary caching issues
      const uniqueName = `recording-${Date.now()}-${Math.floor(Math.random()*1000)}.wav`
      formData.append('audio', audioBlob, uniqueName)
      
      // Add language context to help with accurate transcription
      const userLanguage = userType === 'owner' ? session?.ownerLanguage : session?.guestLanguage
      if (userLanguage) {
        formData.append('language', userLanguage.toLowerCase())
        log.debug('Added language hint for transcription:', userLanguage)
      }
      
      // Add cache-busting query param and disable cache explicitly
      const response = await fetch(apiUrl(`/api/transcribe?t=${Date.now()}`), {
        method: 'POST',
        body: formData,
        cache: 'no-store'
      })
      
      log.info('Transcription response:', { 
        status: response.status, 
        statusText: response.statusText 
      })
      
      if (response.ok) {
        const result = await response.json()
        log.info('Transcription result received')
        const text = result.text?.trim()
        
        if (text) {
          setInputText(text.trim())
          toast.success('Voice converted to text!')
          // Enqueue to ensure first message is reliably sent and subsequent serialize
          enqueueTranscribedText(text.trim())
        } else {
          toast.error('No speech detected. Please try again.')
        }
      } else {
        const errorText = await response.text()
        log.error('Transcription failed:', { 
          status: response.status, 
          statusText: response.statusText,
          errorText 
        })
        if (response.status === 404) {
          toast.error('Voice transcription not available. Please type your message.')
        } else {
          toast.error(`Failed to transcribe audio: ${response.statusText}`)
        }
      }
    } catch (error) {
      log.error('Error transcribing audio:', error)
      toast.error('Failed to process audio. Please check your connection and try again.')
    } finally {
      setIsTranscribing(false)
      toast.dismiss()
    }
  }

  const addTempMessage = (text: string, originalLanguage: string) => {
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
    const tempMsg: any = {
      id: tempId,
      text,
      translatedText: '',
      senderType: userType,
      originalLanguage,
      meaningExplanation: null,
      culturalNotes: null,
      timestamp: new Date().toISOString(),
      _pending: true,
    }
    setMessages(prev => [...prev, tempMsg])
    return tempId
  }

  const pollAndSyncMessages = async () => {
    if (!sessionId) return
    try {
      const res = await fetch(apiUrl(`/api/sessions/${sessionId}/messages`))
      if (res.ok) {
        const serverMsgs = await res.json()
        setMessages(serverMsgs)
        setCurrentCardIndex(Math.max(0, serverMsgs.length - 1))
        setIsFlipped(false)

        // Set a brief "Sent" indicator on the newest own message
        const lastOwn = [...serverMsgs].reverse().find((m: any) => m.senderType === userType)
        if (lastOwn?.id && lastOwn.id !== lastOwnMessageIdRef.current) {
          lastOwnMessageIdRef.current = lastOwn.id
          setJustSentId(lastOwn.id)
          setTimeout(() => setJustSentId(prev => (prev === lastOwn.id ? null : prev)), 1800)
        }
      }
    } catch (_e) {}
  }

  const sendTranscribedMessage = async (text: string): Promise<boolean> => {
    // Allow sending over HTTP even if WebSocket is not connected
    if (!text.trim() || !sessionId) return false

    // Generate optimistic message immediately to build user confidence
    const originalLanguage = userType === 'owner' 
      ? (session?.ownerLanguage || 'English') 
      : (session?.guestLanguage || 'English');
    const tempId = addTempMessage(text.trim(), originalLanguage)

    try {
      const response = await fetch(apiUrl(`/api/sessions/${sessionId}/messages`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          text: text,
          senderType: userType,
          originalLanguage: originalLanguage
        })
      })

      if (response.ok) {
        setInputText('')
        // Reconcile with server by short polling to avoid read-after-write gaps
        await pollAndSyncMessages()
        return true
      } else {
        // Remove optimistic message on failure
        setMessages(prev => prev.filter(m => (m as any).id !== tempId))
        // let caller decide to toast on retry failure
        return false
      }
    } catch (error) {
      console.error('Error sending transcribed message:', error)
      // Remove optimistic message on error
      setMessages(prev => prev.filter(m => (m as any).id !== tempId))
      return false
    }
  }

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording()
    } else {
      startRecording()
    }
  }

  const goPrev = () => setCurrentCardIndex((i) => {
    const next = Math.max(0, i - 1)
    if (next !== i) setIsFlipped(false)
    return next
  })

  const goNext = () => setCurrentCardIndex((i) => {
    const maxIdx = Math.max(0, messages.length - 1)
    const next = Math.min(maxIdx, i + 1)
    if (next !== i) setIsFlipped(false)
    return next
  })

  // Scam detection function
  const checkForScams = async () => {
    // Only check for owners (travelers) and if enough time has passed
    if (userType !== 'owner' || !session || !sessionId) return
    
    const now = Date.now()
    if (now - lastScamCheck < 30000) return // Check at most every 30 seconds
    
    try {
      const response = await fetch(apiUrl(`/api/sessions/${sessionId}/analyze-scams`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      })
      
      // If backend indicates no content, clear any existing alert and exit
      if (response.status === 204) {
        setLastScamCheck(now)
        setScamAlert(null)
        lastScamKeyRef.current = null
        return
      }
      
      if (response.ok) {
        const json = await response.json()
        setLastScamCheck(now)
        
        // Support both wrapped { analysis } and unwrapped responses
        const scamAnalysis = json?.analysis ?? json
        
        if (scamAnalysis?.shouldAlert && scamAnalysis?.warning) {
          // Compute a key per incident: category + latest message id
          const latestId = latestMessageIdRef.current || messages[messages.length - 1]?.id
          const incidentKey = `${scamAnalysis.category || 'generic'}:${latestId || 'none'}`
          
          // Update banner state
          setScamAlert({
            riskLevel: scamAnalysis.riskLevel,
            warning: scamAnalysis.warning,
            suggestions: scamAnalysis.suggestions || [],
            category: scamAnalysis.category,
            latestMessageId: latestId
          })
          
          // Only toast once per incident key to avoid repeated toasts on every poll/message
          if (lastScamKeyRef.current !== incidentKey && dismissedScamKeyRef.current !== incidentKey) {
            lastScamKeyRef.current = incidentKey
            if (scamAnalysis.riskLevel === 'high') {
              toast.error('🚨 Safety Alert: Potential scam detected!', { duration: 8000 })
            } else if (scamAnalysis.riskLevel === 'medium') {
              toast('⚠️ Safety Notice: Please be cautious', { 
                icon: '⚠️',
                duration: 6000 
              })
            }
          }
        } else {
          setScamAlert(null)
          lastScamKeyRef.current = null
        }
      }
    } catch (error) {
      console.error('Scam detection error:', error)
    }
  }

  // Trigger scam detection when messages change
  useEffect(() => {
    if (messages.length > 0 && userType === 'owner') {
      // Debounce scam checking
      const timer = setTimeout(checkForScams, 2000)
      return () => clearTimeout(timer)
    }
  }, [messages.length, userType])

  // Vertical wheel navigation (throttled) to avoid jittery page scroll
  const handleWheelNav = (e: React.WheelEvent) => {
    if (!messages.length) return
    if (Math.abs(e.deltaY) < 10) return
    e.preventDefault()
    if (wheelThrottleRef.current) return
    if (e.deltaY > 0) goNext(); else goPrev()
    wheelThrottleRef.current = window.setTimeout(() => {
      if (wheelThrottleRef.current) window.clearTimeout(wheelThrottleRef.current)
      wheelThrottleRef.current = null
    }, 350) as unknown as number
  }

  // Touch swipe vertical navigation
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartYRef.current = e.touches[0]?.clientY ?? null
  }
  const onTouchMove = (e: React.TouchEvent) => {
    // Prevent the browser from scrolling the page when the user is swiping the card
    if (touchStartYRef.current == null) return
    const dy = (e.touches[0]?.clientY ?? touchStartYRef.current) - touchStartYRef.current
    if (Math.abs(dy) > 8) {
      e.preventDefault()
    }
  }
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartYRef.current == null) return
    const dy = (e.changedTouches[0]?.clientY ?? touchStartYRef.current) - touchStartYRef.current
    if (Math.abs(dy) > 40) {
      if (dy < 0) goNext(); else goPrev()
    }
    touchStartYRef.current = null
  }

  // Session Info Modal Content
  const SessionInfo = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-gray-900">Session Controls</h3>
      
      {session && (
        <div className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Your Role:</span>
            <span className="font-medium capitalize">{userType}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Your Language:</span>
            <span className="font-medium">{session.ownerLanguage}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Guest Language:</span>
            <span className="font-medium">{session.guestLanguage}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Status:</span>
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full bg-white/50 ${
              session.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
            }`}>
              {session.status === 'active' ? 'Guest Joined' : 'Waiting for Guest'}
            </span>
          </div>
        </div>
      )}

      {/* QR Code Section - Show when waiting for guest */}
      {session?.status !== 'active' && (
        <div className="bg-gray-50 p-4 rounded-lg border">
          <p className="text-sm text-gray-600 mb-3 text-center font-medium">Scan QR Code to Join</p>
          <div className="flex justify-center mb-3">
            <QRCode value={`${window.location.origin}/join/${sessionId}`} size={120} />
          </div>
          <div className="text-xs text-gray-500 text-center break-all">
            {`${window.location.origin}/join/${sessionId}`}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <button
          onClick={copySessionLink}
          className={`w-full flex items-center justify-center gap-2 px-4 py-2 bg-${currentScheme.primary}-600 text-white rounded-lg hover:bg-${currentScheme.primary}-700 transition-colors`}
        >
          <Share2 className="w-4 h-4" />
          <span>Copy Invite Link</span>
        </button>
        
        <button
          onClick={terminateSession}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <X className="w-4 h-4" />
          <span>End Session</span>
        </button>
      </div>
    </div>
  )

  return (
    <div className={`flex flex-col h-screen overflow-hidden bg-gradient-to-br ${currentScheme.background}`}>
      {/* Scam Alert Banner - Only shown to travelers (owners) */}
      {userType === 'owner' && scamAlert && scamAlert.latestMessageId === (messages[messages.length - 1]?.id) && (
        <div className={`fixed top-16 left-4 right-4 z-40 rounded-lg p-4 shadow-lg border-l-4 ${
          scamAlert.riskLevel === 'high' 
            ? 'bg-red-50 border-red-500 text-red-800' 
            : scamAlert.riskLevel === 'medium'
            ? 'bg-yellow-50 border-yellow-500 text-yellow-800'
            : 'bg-blue-50 border-blue-500 text-blue-800'
        }`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">
                  {scamAlert.riskLevel === 'high' ? '🚨' : scamAlert.riskLevel === 'medium' ? '⚠️' : 'ℹ️'}
                </span>
                <h3 className="font-semibold">
                  {scamAlert.riskLevel === 'high' ? 'Safety Alert' : 'Travel Notice'}
                </h3>
                {scamAlert.category && (
                  <span className="text-xs px-2 py-1 rounded-full bg-white/50">
                    {scamAlert.category}
                  </span>
                )}
              </div>
              
              <p className="text-sm mb-3 leading-relaxed">
                {scamAlert.warning}
              </p>
              
              {scamAlert.suggestions.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium opacity-75">Safety Tips:</p>
                  <ul className="text-xs space-y-1">
                    {scamAlert.suggestions.map((suggestion, index) => (
                      <li key={index} className="flex items-start gap-1">
                        <span className="text-xs mt-0.5">•</span>
                        <span>{suggestion}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            <button
              onClick={() => {
                // Remember dismissal for this incident so it does not reappear during the same incident
                const key = `${scamAlert?.category || 'generic'}:${scamAlert?.latestMessageId || 'none'}`
                dismissedScamKeyRef.current = key
                setScamAlert(null)
              }}
              className="ml-3 p-1 rounded-full hover:bg-white/20 transition-colors"
              aria-label="Dismiss alert"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* 1. Minimal Header */}
      <header className="flex-shrink-0 flex items-center justify-between px-4 py-2 bg-white/80 backdrop-blur-sm border-b border-gray-200/50">
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
          <span className="font-medium text-gray-900 capitalize">{userType}</span>
        </div>
        
        {userType === 'owner' && (
          <button 
            onClick={() => setShowSessionControls(true)} 
            className="p-2 rounded-full hover:bg-gray-100"
            aria-label="Session controls"
          >
            <Settings className="w-5 h-5 text-gray-600" />
          </button>
        )}
      </header>

      {/* 2. Flashcard View */}
      <main style={{ 
        flexGrow: 1, 
        padding: '20px', 
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 160px)', 
        background: 'linear-gradient(180deg, rgba(255,255,255,0.0), rgba(255,255,255,0.0))',
        minHeight: '400px'
      }}>
        {messages.length > 0 ? (
          <div 
            style={{ width: '100%', maxWidth: '900px', margin: '0 auto', position: 'relative' }}
            onWheel={handleWheelNav}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <div style={{ minHeight: 'clamp(340px, 52vh, 560px)', overflow: 'hidden', touchAction: 'none' as any }}>
              <Flashcard 
                key={(messages[currentCardIndex] as any)?.id || `msg-${currentCardIndex}`}
                message={messages[currentCardIndex]}
                userType={userType}
                isFlipped={isFlipped}
                onFlip={() => setIsFlipped(!isFlipped)}
                justSent={justSentId === (messages[currentCardIndex] as any).id}
              />
            </div>

            <div ref={listEndRef} />
          </div>
        ) : (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: '#6b7280' }}>
            <div>
              <MessageCircle className="mx-auto h-16 w-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No messages yet</h3>
              <p className="text-gray-500">Send a message to start the conversation</p>
            </div>
          </div>
        )}
      </main>

      {/* 3. Input Bar - Voice-First Design */}
      <footer className="flex-shrink-0 p-4 bg-white/80 backdrop-blur-sm border-t border-gray-200/50">
        <div className="flex flex-col items-center gap-4 max-w-2xl mx-auto">
          
          {/* Primary Voice Input - Large Prominent Button */}
          <div className="flex flex-col items-center gap-2">
            <button
              onClick={toggleRecording}
              disabled={!isConnected || isTranscribing}
              className={`relative w-20 h-20 rounded-full transition-all duration-300 flex items-center justify-center shadow-lg ${
                isRecording
                  ? 'bg-red-500 text-white scale-110 animate-pulse'
                  : isTranscribing
                  ? 'bg-yellow-100 text-yellow-600 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 hover:scale-105 active:scale-95'
              }`}
              aria-label={isTranscribing ? 'Processing...' : isRecording ? 'Stop recording' : 'Tap to speak'}
            >
              {isTranscribing ? (
                <div className="w-8 h-8 animate-spin rounded-full border-4 border-yellow-600 border-t-transparent" />
              ) : isRecording ? (
                <>
                  <MicOff className="w-8 h-8 text-white" />
                  <div className="absolute inset-0 rounded-full border-4 border-white/30 animate-ping"></div>
                </>
              ) : (
                <Mic className="w-8 h-8 text-white" />
              )}
            </button>
            
            {/* Voice Status Text */}
            <div className="text-center">
              {isRecording ? (
                <p className="text-red-600 font-medium text-sm animate-pulse">🔴 Recording {Math.floor(recordingSeconds/60).toString().padStart(2,'0')}:{(recordingSeconds%60).toString().padStart(2,'0')} — Tap to stop</p>
              ) : isTranscribing ? (
                <p className="text-yellow-600 font-medium text-sm">⏳ Processing audio...</p>
              ) : (
                <p className="text-gray-600 text-sm font-medium">🎙️ Tap to start. Tap again to stop.</p>
              )}
            </div>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 w-full">
            <div className="flex-1 h-px bg-gray-200"></div>
            <span className="text-xs text-gray-400 font-medium">OR TYPE</span>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>
          
          {/* Secondary Text Input - Smaller, Less Prominent */}
          <div className="w-full relative">
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  sendMessage()
                }
              }}
              placeholder="Or type your message..."
              className={`w-full px-4 py-2.5 pr-12 border rounded-full transition-all focus:outline-none text-sm ${
                `border-gray-300 focus:border-${currentScheme.primary}-400 focus:ring-1 focus:ring-${currentScheme.primary}-100`
              } bg-gray-50 placeholder-gray-400 text-gray-700 focus:bg-white`}
              disabled={!isConnected || isTranscribing || isRecording}
            />
            
            <button
              onClick={sendMessage}
              disabled={actionDisabled}
              className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 rounded-full transition-colors ${
                !actionDisabled
                  ? `bg-${currentScheme.primary}-600 text-white hover:bg-${currentScheme.primary}-700`
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              aria-label="Send message"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </footer>

      {/* Session Controls Modal */}
      {userType === 'owner' && showSessionControls && (
        <div 
          className="absolute inset-0 bg-black/30 z-50 flex items-center justify-center p-4"
          onClick={() => setShowSessionControls(false)}
        >
          <div 
            className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={() => setShowSessionControls(false)} 
              className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-100"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
            <SessionInfo />
          </div>
        </div>
      )}
    </div>
  )
}